import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileType, Loader2, X } from 'lucide-react';
import { PORTAL_SHELL } from './portalTheme';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import {
  DIKTAT_OUTPUT_FORMAT_DEFAULT,
  DIKTAT_OUTPUT_FORMATS,
  diktatOutputFormatHint,
  diktatOutputFormatLabel,
  placeDiktatFormatMenu,
  type DiktatOutputFormat,
} from './portalDiktatFlow';

type DiktatOutputFormatMenuProps = {
  open: boolean;
  busy?: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onChoose: (format: DiktatOutputFormat) => void;
  compact?: boolean;
};

/**
 * Klasse DIKTAT_OUTPUT_FORMAT + POPOVER_DISMISS_OUTSIDE.
 * Format-Menü (PDF / DOCX / TXT) — Overlay per createPortal, flip nach oben
 * wenn unten kein Platz (kein overflow-hidden-Clip am Diktate-Ordner).
 */
export const DiktatOutputFormatMenu: React.FC<DiktatOutputFormatMenuProps> = ({
  open,
  busy = false,
  error = null,
  onOpenChange,
  onChoose,
  compact = false,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ left: number; top: number; flipUp: boolean } | null>(null);

  usePopoverDismissOutside({
    open,
    onClose: () => onOpenChange(false),
    rootRef: menuRef,
    ignoreCloseRef: triggerRef,
  });

  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return undefined;
    }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const placed = placeDiktatFormatMenu(
        rect,
        { width: window.innerWidth, height: window.innerHeight },
        compact ? 'right' : 'left',
      );
      setAnchor(placed);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [compact, open]);

  const menu =
    open && anchor && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-testid="portal-diktat-format-overlay"
            data-portal-diktat-format-overlay="1"
            data-flip-up={anchor.flipUp ? '1' : '0'}
            className="fixed z-[90] rounded-lg border border-violet-400/30 bg-[#07070a] shadow-xl p-1 min-w-[12rem]"
            style={{ left: anchor.left, top: anchor.top }}
          >
            {DIKTAT_OUTPUT_FORMATS.map((format) => (
              <button
                key={format}
                type="button"
                role="menuitem"
                data-testid={`portal-diktat-format-${format}`}
                disabled={busy}
                onClick={() => {
                  onChoose(format);
                  onOpenChange(false);
                }}
                className="w-full text-left px-2.5 py-1.5 rounded-md text-[12px] text-slate-200 hover:bg-violet-600/30 disabled:opacity-50"
              >
                <span className="font-semibold">{diktatOutputFormatLabel(format)}</span>
                {format === DIKTAT_OUTPUT_FORMAT_DEFAULT ? (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-emerald-400/90">Standard</span>
                ) : null}
                <span className={`block ${PORTAL_SHELL.textMuted} text-[10px] leading-snug`}>
                  {diktatOutputFormatHint(format)}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative overflow-visible">
      <button
        ref={triggerRef}
        type="button"
        data-testid="portal-diktat-format-menu"
        disabled={busy}
        onClick={() => onOpenChange(!open)}
        className="relative h-7 w-7 shrink-0 p-0 [perspective:280px]"
        aria-expanded={open}
        aria-haspopup="menu"
        title="Format wählen (PDF, DOCX, TXT)"
        aria-label="Format wählen"
      >
        {busy ? (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
          </span>
        ) : (
          <span className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]" style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}>
            <span className="absolute inset-0 inline-flex items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 [backface-visibility:hidden]">
              <FileType className="w-3.5 h-3.5" aria-hidden />
            </span>
            <span
              className="absolute inset-0 inline-flex items-center justify-center rounded-md bg-violet-600 text-white [backface-visibility:hidden]"
              style={{ transform: 'rotateX(180deg)' }}
            >
              <X className="w-3.5 h-3.5" aria-hidden />
            </span>
          </span>
        )}
      </button>
      {menu}
      {error ? <p className="m-0 mt-1 text-[11px] text-amber-200/90">{error}</p> : null}
    </div>
  );
};
